/**
 * Вкладка «Місяць»: зведення P&L і профіль доби.
 *
 * Профіль, а не 31×24 клітинок: саме форма доби визначає коефіцієнт захвату —
 * скільки з BASE станція справді забирає, якщо денну «яму» переносити УЗЕ у
 * вечірній пік. Місяць згортаємо по годині доби: ціни — середні, обсяги й
 * виручка — суми.
 *
 * Компонування «графік + таблиця під ним» — те саме, що в «Плановому доході»
 * і «Торговому графіку»: заголовок секції поза плиткою, графік у своїй плитці,
 * нижче ТРАНСПОНОВАНА таблиця (години в стовпцях, показники рядками) через
 * спільний FinHourlyTable, під нею — рядок пояснень. Повторне використання, а
 * не власна таблиця: так липкі крайні стовпці, підсвітка колонки під курсором
 * і автоскрол поводяться однаково по всьому кабінету.
 */
import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, CartesianGrid, Tooltip,
} from 'recharts';
import { Clock, Coins, Tag, Sun, BatteryCharging, Banknote } from 'lucide-react';
import { useChartTheme } from '@/components/base/chartTheme';
import ChartLegend from '@/components/base/ChartLegend';
import FinHourlyTable from '@/pages/financial/result/FinHourlyTable';
import type { FinCol, FinRow } from '@/pages/financial/result/FinHourlyTable';
import type { MonthRow, HourRow } from '../types';
import { mln, n0, n1, pct } from '../types';

/**
 * Рядок водоспаду P&L. Сегмент починається там, де закінчився попередній, тож
 * від виручки до потоку видно сходинку: скільки забрали змінні, OPEX, фінансові
 * і податок. Ширина сегмента — частка від виручки, і більше вона нічого не
 * означає; жодної декорації понад це.
 */
function FlowRow({ label, value, unit, startPct, lenPct, kind, note }: {
  label: string; value: string; unit: string;
  startPct: number; lenPct: number;
  /** result — підсумок (виручка, EBITDA, прибуток, потік); cost — відрахування. */
  kind: 'result' | 'cost' | 'negative';
  note?: string;
}) {
  const color = kind === 'result' ? 'var(--ok)'
    : kind === 'negative' ? 'var(--warn)' : 'var(--series-3)';
  const strong = kind !== 'cost';
  return (
    <div className="py-[7px]">
      <div className="flex items-baseline justify-between gap-3">
        <span style={{
          fontSize: strong ? 12.5 : 12,
          fontWeight: strong ? 600 : 400,
          color: kind === 'cost' ? 'var(--text-muted)' : 'var(--text)',
          paddingLeft: kind === 'cost' ? 12 : 0,
        }}>{label}</span>
        <span className="shrink-0 flex items-baseline">
          <span className="font-display" style={{
            fontSize: 14, fontWeight: strong ? 600 : 400,
            color: kind === 'cost' ? 'var(--text-muted)' : 'var(--text)',
            fontVariantNumeric: 'tabular-nums', minWidth: 62, textAlign: 'right',
          }}>{value}</span>
          <span className="text-[10.5px]" style={{ color: 'var(--text-muted)', minWidth: 56, paddingLeft: 6 }}>
            {unit}
          </span>
        </span>
      </div>
      {/* Доріжка водоспаду: тонка, без рамки — глибину дає сам сегмент. */}
      <div className="mt-1.5" style={{ height: 5, borderRadius: 3, background: 'var(--surface-2)' }}>
        <div style={{
          marginLeft: `${Math.max(0, Math.min(100, startPct))}%`,
          width: `${Math.max(0.8, Math.min(100, lenPct))}%`,
          height: 5, borderRadius: 3, background: color,
          opacity: kind === 'cost' ? 0.55 : 0.95,
        }} />
      </div>
      {note && (
        <div className="text-[10.5px] mt-1" style={{ color: 'var(--text-dim)' }}>{note}</div>
      )}
    </div>
  );
}

/** Метр рентабельності: заповнена доріжка + число. Частки, не суми. */
function Meter({ label, share, text, note }: {
  label: string; share: number | null; text: string; note?: string;
}) {
  const v = share == null ? null : Math.max(0, Math.min(1, share));
  const color = share != null && share < 0 ? 'var(--warn)' : 'var(--ok)';
  return (
    <div className="py-2.5">
      <div className="flex items-baseline justify-between gap-3">
        <span className="text-[12px]" style={{ color: 'var(--text)' }}>{label}</span>
        <span className="font-display text-[14px] shrink-0"
          style={{ color: 'var(--text)', fontVariantNumeric: 'tabular-nums' }}>{text}</span>
      </div>
      <div className="mt-1.5" style={{ height: 5, borderRadius: 3, background: 'var(--surface-2)' }}>
        <div style={{
          width: `${v == null ? 0 : v * 100}%`, height: 5, borderRadius: 3,
          background: color, opacity: 0.95,
        }} />
      </div>
      {note && <div className="text-[10.5px] mt-1" style={{ color: 'var(--text-dim)' }}>{note}</div>}
    </div>
  );
}

export function MonthSummary({ pl, plFrom }: { pl: MonthRow | null; plFrom: string }) {
  const { t } = useTranslation();
  if (!pl) {
    return (
      <div className="text-[12.5px] py-6 text-center" style={{ color: 'var(--text-muted)' }}>
        {t('ak_bud2_there_is_no_approved_budget_version_fo')}
      </div>
    );
  }

  const rev = pl.revenue || 0;
  // Масштаб водоспаду — виручка. Якщо її немає, сегменти не малюємо.
  const pc = (v: number) => (rev > 0 ? (Math.abs(v) / rev) * 100 : 0);
  // Зміщення рахуємо ЗІ ЗНАКОМ: тіло кредиту буває більшим за чистий прибуток,
  // і залишок іде в мінус. Тоді сегмент притискається до нуля й обрізається по
  // доступному залишку — інакше він вилазив за межу доріжки.
  const pos = (v: number) => (rev > 0 ? (v / rev) * 100 : 0);
  const v = (x: number | null | undefined) => x || 0;

  // Накопичувач іде зверху вниз: кожне відрахування починається там, де
  // закінчився попередній залишок.
  let run = rev;
  const steps: {
    label: string; amount: number; unit: string;
    start: number; len: number; kind: 'result' | 'cost' | 'negative'; note?: string;
  }[] = [];
  const push = (label: string, amount: number, kind: 'result' | 'cost' | 'negative', note?: string) => {
    if (kind === 'cost') {
      const start = Math.max(0, pos(run - Math.abs(amount)));
      const len = Math.max(0, Math.min(pc(amount), Math.max(0, pos(run)) - start));
      steps.push({ label, amount, unit: t('ak_bud2_uah_mn'), start, len, kind, note });
      run -= Math.abs(amount);
    } else {
      steps.push({ label, amount, unit: t('ak_bud2_uah_mn'), start: 0, len: pc(amount), kind, note });
    }
  };

  push(t('ak_bud2_revenue'), rev, 'result',
    pl.bessEffect ? t('ak_bud2_incl_bess_effect_mn', { v: mln(pl.bessEffect) }) : undefined);
  push(t('ak_bud2_variable_costs'), v(pl.variable), 'cost', t('ak_bud2_supplier_margin_3_imbalances_2_5'));
  push(t('ak_bud2_fixed_opex'), v(pl.opexFixed), 'cost');
  run = v(pl.ebitda);                    // синхронізуємо з фактичним EBITDA
  push('EBITDA', v(pl.ebitda), 'result');
  push(t('ak_bud2_finance_costs'), v(pl.finance), 'cost', t('ak_bud2_interest_fee_account_servicing'));
  push(t('ak_bud2_income_tax'), v(pl.tax), 'cost');
  run = v(pl.netProfit);
  push(t('ak_bud2_net_profit'), v(pl.netProfit), 'result');
  push(t('ak_bud2_loan_principal_repayment'), v(pl.principal), 'cost');
  const flow = v(pl.cashFlow);
  steps.push({
    label: t('ak_bud2_cash_flow_after_repayment'), amount: flow, unit: t('ak_bud2_uah_mn'),
    start: 0, len: pc(flow), kind: flow < 0 ? 'negative' : 'result',
    note: flow < 0 ? t('ak_bud2_month_with_a_principal_payment_covered') : undefined,
  });

  const margin = rev > 0 ? v(pl.ebitda) / rev : null;
  const netMargin = rev > 0 ? v(pl.netProfit) / rev : null;
  const bessShare = rev > 0 && pl.bessEffect != null ? pl.bessEffect / rev : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[1.55fr_1fr] gap-x-8">
      <div>
        <div className="text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud2_from_revenue_to_cash_flow')}
        </div>
        {steps.map((st) => (
          <FlowRow key={st.label} label={st.label}
            value={mln(st.amount)} unit={st.unit}
            startPct={st.start} lenPct={st.len} kind={st.kind} note={st.note} />
        ))}
        <div className="text-[10.5px] mt-2" style={{ color: 'var(--text-dim)' }}>
          {t('ak_bud2_segment_width_is_the_share_of_the_mont')}
        </div>
      </div>

      {/* Тонка межа замість другої плитки: один рівень поверхні (§3). */}
      <div className="lg:pl-8 mt-5 lg:mt-0"
        style={{ borderLeft: '1px solid var(--line)' }}>
        <div className="text-[11px] mb-1" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud2_profitability_and_margin')}
        </div>
        <Meter label={t('ak_bud2_ebitda_margin')} share={margin} text={pct(margin)} />
        <Meter label={t('ak_bud2_net_margin')} share={netMargin} text={pct(netMargin)} />
        <Meter label={t('ak_bud2_safety_margin')} share={pl.safetyMargin} text={pct(pl.safetyMargin)}
          note={t('ak_bud2_break_even_revenue_mn_break_even_price', { a: mln(pl.beRevenue), b: n0(pl.beBasePrice) })} />
        <Meter label={t('ak_bud2_bess_share_of_revenue')} share={bessShare} text={pct(bessShare)}
          note={pl.bessEffect ? t('ak_bud2_mn_of_mn', { a: mln(pl.bessEffect), b: mln(rev) }) : undefined} />

        <div className="mt-4 pt-3 text-[10.5px]"
          style={{ borderTop: '1px solid var(--line)', color: 'var(--text-dim)' }}>
          {plFrom === 'year'
            ? t('ak_bud2_source_monthly_slice_of_the_approved_a')
            : t('ak_bud2_source_approved_version_of_the_monthly')}
        </div>
      </div>
    </div>
  );
}

interface Agg {
  hh: string;
  rdn: number | null; idm: number | null;
  exportMwh: number | null; revenue: number | null;
  /** Заряд і розряд ОКРЕМО: знакова сума за місяць (−798 + 702 = −96) не
      означає нічого корисного, а два числа — означають. */
  chargeMwh: number | null; dischargeMwh: number | null;
}

/**
 * Згортка місяця по годині доби. Ціни — середні (дає місячний BASE профілю),
 * обсяги й виручка — суми. Підпис години астрономічний («11» = 11:00–12:00),
 * бо «яма» і «пік» у регламенті задані астрономічними годинами.
 */
function aggregate(hours: HourRow[]): Agg[] {
  const acc = new Map<number, { rdn: number[]; idm: number[]; exp: number; ch: number; di: number; rev: number; hasVol: boolean }>();
  for (const h of hours) {
    const a = acc.get(h.pos) || { rdn: [], idm: [], exp: 0, ch: 0, di: 0, rev: 0, hasVol: false };
    if (h.rdn != null) a.rdn.push(h.rdn);
    if (h.idm != null) a.idm.push(h.idm);
    if (h.exportKwh != null) { a.exp += h.exportKwh; a.hasVol = true; }
    if (h.bessKwh != null) { if (h.bessKwh < 0) a.ch += -h.bessKwh; else a.di += h.bessKwh; }
    if (h.revenue != null) a.rev += h.revenue;
    acc.set(h.pos, a);
  }
  const avg = (xs: number[]) => (xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null);
  return Array.from({ length: 24 }, (_, i) => i + 1).map((pos) => {
    const a = acc.get(pos);
    return {
      hh: String(pos - 1).padStart(2, '0'),
      rdn: a ? avg(a.rdn) : null,
      idm: a ? avg(a.idm) : null,
      exportMwh: a && a.hasVol ? a.exp / 1000 : null,
      chargeMwh: a && a.hasVol ? a.ch / 1000 : null,
      dischargeMwh: a && a.hasVol ? a.di / 1000 : null,
      revenue: a && a.rev ? a.rev : null,
    };
  });
}

export function HourlyProfile({ hours, has, monthLabel, planMonthMwh, planMonthRevenue }: {
  hours: HourRow[]; has: { price: boolean; plan: boolean }; monthLabel: string;
  /** Місячний план із P&L — щоб видно було, що сума погодинного плану його НЕ
      повторює, коли покриття неповне. */
  planMonthMwh?: number | null; planMonthRevenue?: number | null;
}) {
  const ct = useChartTheme();
  const rows = useMemo(() => aggregate(hours), [hours]);
  const { t } = useTranslation();
  // Година під курсором на графіку підсвічує ту саму колонку в таблиці нижче —
  // так само, як у «Плановому доході» й на /dispatch/forecast-mms.
  const [activeKey, setActiveKey] = useState<string | number | null>(null);

  const header = (
    <div className="flex items-center justify-between gap-3 mb-3">
      <div className="flex items-center gap-2">
        <Clock size={17} strokeWidth={1.75} style={{ color: 'var(--text-muted)' }} />
        <h3 className="font-display text-base font-semibold" style={{ color: 'var(--text)' }}>
          {t('ak_bud2_daily_profile_prices_and_release_plan')}
        </h3>
      </div>
      <span className="text-[11px] shrink-0 text-right" style={{ color: 'var(--text-dim)' }}>
        {t('ak_bud2_folded_by_hour_of_day', { m: monthLabel })}
      </span>
    </div>
  );

  if (!has.price && !has.plan) {
    return (
      <section>
        {header}
        <div className="tile p-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
          {t('ak_bud2_there_is_no_hourly_forecast_for_this_m')}
        </div>
      </section>
    );
  }

  const legend = [
    { label: t('ak_bud2_dam_price_forecast'), color: ct.series.s3 },
    { label: t('ak_bud2_idm_price_forecast'), color: ct.series.s2, dashed: true },
    ...(has.plan ? [{ label: t('ak_bud2_plan_release'), color: ct.series.ok, bar: true }] : []),
  ];

  const cols: FinCol[] = [
    ...rows.map((r) => ({ key: r.hh, label: r.hh })),
    { key: 'sum', label: t('ak_bud2_total'), strong: true },
  ];

  const simpleAvg = (f: 'rdn' | 'idm') => {
    const xs = rows.map((r) => r[f]).filter((x): x is number => x != null);
    return xs.length ? xs.reduce((s, x) => s + x, 0) / xs.length : null;
  };
  const sumOf = (f: 'exportMwh' | 'chargeMwh' | 'dischargeMwh' | 'revenue') =>
    rows.reduce((s, r) => s + (r[f] || 0), 0);

  /**
   * «Всього» для рядка ціни — середньозважена ВІДПУСКОМ ціна (capture), а не
   * проста середня: саме вона множиться на відпуск і дає виручку, тож стовпець
   * сходиться арифметично (відпуск × ціна = виручка) і не потребує пояснень.
   * Для РДН беремо точне Σвиручки / Σвідпуску. Для ВДР виручки немає, тож
   * важимо профілем: Σ(відпуск_год × ціна_год) / Σвідпуск.
   * Без плану відпуску зважувати нічим — тоді проста середня за добу (= BASE).
   */
  const totalExport = sumOf('exportMwh');
  const capture = (f: 'rdn' | 'idm'): number | null => {
    if (!has.plan || totalExport <= 0) return simpleAvg(f);
    if (f === 'rdn') {
      const rev = sumOf('revenue');
      return rev > 0 ? rev / totalExport : simpleAvg(f);
    }
    let num = 0;
    for (const r of rows) if (r[f] != null && r.exportMwh) num += r.exportMwh * (r[f] as number);
    return num > 0 ? num / totalExport : simpleAvg(f);
  };
  const priceTotalWeighted = has.plan && totalExport > 0;
  // Скільки діб місяця реально мають погодинний план — без цього «Всього»
  // читалось би як місячний підсумок, хоч покриває лише частину діб.
  const planDays = new Set(hours.filter((h) => h.exportKwh != null).map((h) => h.ts.slice(0, 10))).size;
  const monthDays = new Set(hours.map((h) => h.ts.slice(0, 10))).size;

  const tableRows: FinRow[] = [
    {
      name: t('ak_bud2_dam_price_uah_mwh'), Icon: Coins, color: ct.series.s3,
      cells: [...rows.map((r) => ({ v: n0(r.rdn) })), { v: n0(capture('rdn')) }],
    },
    {
      name: t('ak_bud2_idm_price_uah_mwh'), Icon: Tag, color: 'var(--text-muted)',
      cells: [...rows.map((r) => ({ v: n0(r.idm) })), { v: n0(capture('idm')) }],
    },
    ...(has.plan ? [
      {
        name: t('ak_bud2_plan_release_mwh'), Icon: Sun, color: ct.series.ok,
        cells: [...rows.map((r) => ({ v: n1(r.exportMwh) })), { v: n1(sumOf('exportMwh')) }],
      },
      {
        // Заряд — споживання (бурштин за догмою), окремим рядком від розряду:
        // їхня знакова сума нічого не означає, а два числа зіставні з планом.
        name: t('ak_bud2_bess_charge_mwh'), Icon: BatteryCharging, color: 'var(--warn)',
        cells: [
          ...rows.map((r) => ({ v: r.chargeMwh ? n1(-r.chargeMwh) : n1(r.chargeMwh) })),
          { v: n1(-sumOf('chargeMwh')) },
        ],
      },
      {
        // Розряд — віддача в мережу: другий зелений (--ok-2) за догмою.
        name: t('ak_bud2_bess_discharge_mwh'), Icon: BatteryCharging, color: 'var(--ok-2)',
        cells: [
          ...rows.map((r) => ({ v: n1(r.dischargeMwh) })),
          { v: n1(sumOf('dischargeMwh')) },
        ],
      },
      {
        name: t('ak_bud2_plan_revenue_uah_k'), Icon: Banknote, strong: true,
        cells: [
          ...rows.map((r) => ({ v: r.revenue == null ? '—' : n0(r.revenue / 1000) })),
          { v: n0(sumOf('revenue') / 1000) },
        ],
      },
    ] : []),
  ];

  return (
    <section>
      {header}

      <div className="tile p-4 mb-3">
        <div style={{ height: 268 }}>
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={rows} margin={{ top: 6, right: 8, left: 0, bottom: 0 }}
              onMouseMove={(st: { activeLabel?: string | number }) => setActiveKey(st?.activeLabel ?? null)}
              onMouseLeave={() => setActiveKey(null)}>
              <CartesianGrid {...ct.gridProps} />
              <XAxis dataKey="hh" {...ct.axis} interval={1} />
              <YAxis yAxisId="p" {...ct.axis} width={54} label={ct.axisLabel(t('ak_bud2_uah_mwh'))} />
              {has.plan && (
                <YAxis yAxisId="v" orientation="right" {...ct.axis} width={44}
                  label={ct.axisLabel(t('ak_bud2_mwh_2'), 90)} />
              )}
              <Tooltip {...ct.tooltip} cursor={ct.cursorLine}
                labelFormatter={(l: string) => `${l}:00`}
                formatter={(v: number | string, name: string) => {
                  const x = typeof v === 'number' ? v : Number(v);
                  if (name === t('ak_bud2_plan_release')) return [t('ak_bud2_mwh', { v: n1(x) }), name];
                  return [t('ak_bud2_uah_mwh_2', { v: n0(x) }), name];
                }} />
              {has.plan && (
                <Bar yAxisId="v" dataKey="exportMwh" name={t('ak_bud2_plan_release')}
                  {...ct.bar} fill={ct.series.ok} stroke={ct.series.ok} />
              )}
              <Line yAxisId="p" dataKey="rdn" name={t('ak_bud2_dam_price')}
                {...ct.line} stroke={ct.series.s3} />
              <Line yAxisId="p" dataKey="idm" name={t('ak_bud2_idm_price')}
                {...ct.lineThin} stroke={ct.series.s2} strokeDasharray="4 3" />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
        <ChartLegend items={legend} />
      </div>

      <FinHourlyTable firstLabel={t('ak_bud2_metric_hour')} cols={cols} rows={tableRows}
        minWidth={940} activeColKey={activeKey} cornerIcon={Clock} />

      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-1 mt-3 text-[11px]"
        style={{ color: 'var(--text-dim)' }}>
        <span>{t('ak_bud2_clock_hour')} <b style={{ color: 'var(--text-muted)' }}>11</b> = 11:00–12:00</span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>
          <b style={{ color: 'var(--text-muted)' }}>{t('ak_bud2_prices')}</b> {t('ak_bud2_monthly_averages')}
          {priceTotalWeighted
            ? t('ak_bud2_total_weighted_by_release_release_pric')
            : t('ak_bud2_total_simple_daily_average_as_there_is')}
        </span>
        <span style={{ opacity: 0.4 }}>·</span>
        <span>
          <b style={{ color: 'var(--text-muted)' }}>{t('ak_bud2_volumes_and_revenue')}</b> {t('ak_bud2_totals')}
          {has.plan && planDays < monthDays
            ? t('ak_bud2_for_of_days_that_have_an_hourly_plan', { p: planDays, m: monthDays })
            : t('ak_bud2_for_the_month')}
        </span>
        {has.plan && planMonthMwh != null && planDays < monthDays && (
          <>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ color: 'var(--warn)' }}>
              {planMonthRevenue != null ? t('ak_bud2_so_total_differs_from_the_monthly_p_l', { mwh: n0(planMonthMwh), rev: mln(planMonthRevenue) }) : t('ak_bud2_so_total_differs_from_the_monthly_p_l_2', { mwh: n0(planMonthMwh) })}
            </span>
          </>
        )}
        {!has.plan && (
          <>
            <span style={{ opacity: 0.4 }}>·</span>
            <span style={{ color: 'var(--warn)' }}>
              {t('ak_bud2_there_is_no_hourly_release_plan_for_th')}
            </span>
          </>
        )}
      </div>
    </section>
  );
}
